import type { FormikHelpers } from 'formik';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import type { PhotoCandidateItem } from 'api/apiProducts';
import {
    deleteCloudflareFiles,
    fetchCloudflareContentBlob,
    getCloudflareUploadUrls,
    linkCloudflareFiles,
    type CloudUploadScope,
} from 'api/apiCloudflare';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { toStoredCloudFile } from 'modules/files';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { fileToWebpFile } from 'utils/helpers/imgFunctions/fileToWebpFile';
import type { ProductEditType } from '../../types';

const PHOTO_WEBP_QUALITY = 0.8;
const PHOTO_MAX_SIZE = 512;
const PHOTO_SEARCH_MIN_NAME = 10;

type PhotoBusyAction = 'upload' | 'remove' | 'search' | 'pick' | 'crop';

interface UseProductPhotoParams {
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const isRejectedBlob = (blob: Blob | null): boolean => {
    if (!blob) return true;
    const blobType = String(blob.type || '').toLowerCase();
    if (!blobType) return false;
    if (blobType.includes('text/') || blobType.includes('application/json')) return true;
    return !blobType.startsWith('image/');
};

const useProductPhoto = ({ setFieldValue, values, ensureSaved, fetchProducts }: UseProductPhotoParams) => {
    const { t } = useTranslation(['Products', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [busyAction, setBusyAction] = useState<PhotoBusyAction | null>(null);
    const [pickingId, setPickingId] = useState<string | null>(null);
    const busy = busyAction !== null;
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<PhotoCandidateItem[]>([]);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [hideAutoPhoto, setHideAutoPhoto] = useState(false);
    const skipSavedLoad = useRef(false);
    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const cropSrcRef = useRef<string | null>(null);
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const savedUrl = String(values.prodPhoto || '').trim();
    const shownUrl = previewUrl || savedUrl;
    const productName = String(values.prodName || '').trim();
    const canSearch = productName.length >= PHOTO_SEARCH_MIN_NAME;

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    useEffect(() => {
        return () => {
            if (cropSrcRef.current) URL.revokeObjectURL(cropSrcRef.current);
        };
    }, []);

    useEffect(() => {
        const productId = Number(values?.ID || 0);
        if (!canEdit || productId <= 0 || skipSavedLoad.current) {
            setLoadingSaved(false);
            return;
        }
        let cancelled = false;
        setLoadingSaved(true);
        void apiProduct
            .loadPhotoCandidates(productId)
            .then((result) => {
                if (cancelled || skipSavedLoad.current || result.stat === false) return;
                setCandidates(result.items);
            })
            .finally(() => {
                if (!cancelled) setLoadingSaved(false);
            });
        return () => {
            cancelled = true;
        };
    }, [canEdit, values?.ID]);

    const replaceCropSrc = (next: string | null) => {
        const current = cropSrcRef.current;
        if (current && current !== next) URL.revokeObjectURL(current);
        cropSrcRef.current = next;
        setCropSrc(next);
    };

    const clearCrop = () => {
        replaceCropSrc(null);
    };

    const resolveProductId = async (): Promise<number | false> => {
        const currentId = Number(values?.ID || 0);
        if (currentId > 0) return currentId;
        const scope = await ensureSaved();
        if (!scope) return false;
        const createdId = Number(scope.productId || 0);
        return createdId > 0 ? createdId : false;
    };

    const uploadPhoto = async (productId: number, file: File): Promise<string | null> => {
        const optimizedFile = await fileToWebpFile(file, PHOTO_WEBP_QUALITY, PHOTO_MAX_SIZE, PHOTO_MAX_SIZE);
        const uploadConfigs = await getCloudflareUploadUrls(
            [
                {
                    fileName: optimizedFile.name,
                    contentType: optimizedFile.type || 'application/octet-stream',
                    fileSize: optimizedFile.size,
                },
            ],
            { productId },
        );
        const uploadConfig = uploadConfigs?.[0];
        if (!uploadConfig) return null;
        const uploaded = await uploadFileWithProgress(optimizedFile, uploadConfig);
        if (!uploaded.success) return null;
        const storedFile: LeadCloudFileType = toStoredCloudFile(uploaded.config);
        const linked = await linkCloudflareFiles([storedFile], { productId });
        if (!linked || !storedFile.publicUrl) return null;
        return storedFile.publicUrl;
    };

    const commitPhoto = async (productId: number, file: File, localUrl: string | null): Promise<boolean> => {
        const previousUrl = savedUrl;
        const publicUrl = await uploadPhoto(productId, file);
        if (!publicUrl) {
            if (localUrl) {
                setPreviewUrl(null);
                URL.revokeObjectURL(localUrl);
            }
            showToast(t('common:messError'), 'danger');
            return false;
        }

        const saved = await apiProduct.savePhoto(productId, publicUrl);
        if (!saved) {
            await deleteCloudflareFiles({ publicUrls: [publicUrl], scope: { productId } });
            if (localUrl) {
                setPreviewUrl(null);
                URL.revokeObjectURL(localUrl);
            }
            showToast(t('common:messError'), 'danger');
            return false;
        }

        setFieldValue('prodPhoto', publicUrl);
        if (localUrl) {
            setPreviewUrl(null);
            URL.revokeObjectURL(localUrl);
        }
        await fetchProducts(true);

        if (previousUrl && previousUrl !== publicUrl) {
            try {
                await deleteCloudflareFiles({ publicUrls: [previousUrl], scope: { productId } });
            } catch {
                // Старий файл не блокує вже збережене фото
            }
        }
        showToast(t('messPhotoSaved'), 'success');
        return true;
    };

    const openPicker = () => {
        if (!canEdit || busy || !fileRef.current) return;
        fileRef.current.value = '';
        fileRef.current.click();
    };

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        if (!file || !canEdit) return;

        const localUrl = URL.createObjectURL(file);
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return localUrl;
        });
        setBusyAction('upload');

        try {
            const productId = await resolveProductId();
            if (!productId) {
                setPreviewUrl(null);
                URL.revokeObjectURL(localUrl);
                return;
            }
            await commitPhoto(productId, file, localUrl);
        } catch {
            setPreviewUrl(null);
            URL.revokeObjectURL(localUrl);
            showToast(t('common:messError'), 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    const handleRemove = async () => {
        if (!canEdit || busy || !savedUrl) return;
        const productId = Number(values?.ID || 0);
        if (productId <= 0) {
            setFieldValue('prodPhoto', '');
            return;
        }

        setBusyAction('remove');
        try {
            const saved = await apiProduct.savePhoto(productId, '');
            if (!saved) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            setFieldValue('prodPhoto', '');
            await fetchProducts(true);
            try {
                await deleteCloudflareFiles({ publicUrls: [savedUrl], scope: { productId } });
            } catch {
                // Поле вже очищене
            }
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    const handleSearch = async () => {
        if (!canEdit || busy || loadingSaved || !canSearch) return;
        clearCrop();
        setBusyAction('search');
        skipSavedLoad.current = true;
        try {
            const productId = await resolveProductId();
            if (!productId) return;
            const result = await apiProduct.searchPhotoCandidates(productName, productId);
            if (result.stat === false) {
                const keepGrid = result.errCode === 'photo_rate_limit' || result.errCode === 'photo_daily_limit';
                if (!keepGrid) setCandidates([]);
                const searchError =
                    result.errCode === 'photo_name_too_short'
                        ? t('messPhotoNameMin', { min: PHOTO_SEARCH_MIN_NAME })
                        : result.errCode === 'photo_not_found'
                          ? t('messPhotoNotFound')
                          : result.errCode === 'photo_rate_limit'
                            ? t('messPhotoRateLimit')
                            : result.errCode === 'photo_daily_limit'
                              ? t('messPhotoDailyLimit')
                              : t('messPhotoSearchUnavailable');
                showToast(searchError, 'danger');
                return;
            }
            setCandidates(result.items);
            setHideAutoPhoto(true);
        } catch {
            setCandidates([]);
            showToast(t('messPhotoSearchUnavailable'), 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    const handlePick = async (candidateId: string) => {
        if (!canEdit || busy) return;
        setPickingId(candidateId);
        setBusyAction('pick');
        try {
            const blob = await apiProduct.fetchPhotoCandidate(candidateId);
            if (isRejectedBlob(blob)) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            const productId = await resolveProductId();
            if (!productId) return;
            const blobType = String(blob?.type || '').toLowerCase();
            const file = new File([blob as Blob], 'product-photo', { type: blobType || 'image/jpeg' });
            const saved = await commitPhoto(productId, file, null);
            if (saved) clearCrop();
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setPickingId(null);
            setBusyAction(null);
        }
    };

    const handleOpenCrop = async () => {
        if (!canEdit || busy || !savedUrl) return;
        setBusyAction('crop');
        try {
            const blob = await fetchCloudflareContentBlob(savedUrl);
            if (isRejectedBlob(blob) || !String(blob?.type || '').toLowerCase().startsWith('image/')) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            replaceCropSrc(URL.createObjectURL(blob as Blob));
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    const handleCropBack = () => {
        if (busy) return;
        clearCrop();
    };

    const handleCropApply = async (file: File) => {
        if (!canEdit || busy || !savedUrl) return;
        setBusyAction('crop');
        try {
            const productId = await resolveProductId();
            if (!productId) return;
            const saved = await commitPhoto(productId, file, null);
            if (saved) clearCrop();
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    return {
        fileRef,
        busy,
        busyAction,
        pickingId,
        candidates,
        loadingSaved,
        hideAutoPhoto,
        cropSrc,
        canEdit,
        savedUrl,
        shownUrl,
        productName,
        canSearch,
        photoSearchMinName: PHOTO_SEARCH_MIN_NAME,
        openPicker,
        handleChange,
        handleRemove,
        handleSearch,
        handlePick,
        handleOpenCrop,
        handleCropBack,
        handleCropApply,
    };
};

export default useProductPhoto;
